import React, { useCallback, useEffect, useState } from "react";
import { getLogsPaginado, exportLogsToExcel } from "../../../componentes/http/actions/ConfiguracionAction";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Grid,
  Typography,
  Box,
  Snackbar,
  Alert,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import es from 'date-fns/locale/es';
import DownloadIcon from '@mui/icons-material/Download';

const GestorLogs = () => {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [filtros, setFiltros] = useState({
    fechaDesde: null,
    fechaHasta: null,
    proceso: ""
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Lista de procesos para el selector
  const procesosOptions = [
  "", "INICIO", "CLIENTE", "CLIENTE_RES", "ENVIO_PREP", 
  "ENVIO_IND", "ENVIO_LOTE", "NORMATIVAS", "ERROR", "FIN"
];

  // Función para formatear fechas para enviar al backend
  const formatearFiltrosParaEnvio = (filtros) => {
    return {
      ...filtros,
      fechaDesde: filtros.fechaDesde ? filtros.fechaDesde.toISOString() : null,
      fechaHasta: filtros.fechaHasta ? filtros.fechaHasta.toISOString() : null
    };
  };

  // Función centralizada para cargar logs con filtros actualizados
  const loadLogsWithFilters = useCallback((currentPage, currentRowsPerPage, currentFiltros) => {
    setIsLoading(true);
    
    // Formatear los filtros antes de enviarlos
    const filtrosFormateados = formatearFiltrosParaEnvio(currentFiltros);
    
    getLogsPaginado(currentPage + 1, currentRowsPerPage, filtrosFormateados, localStorage.getItem("token"))
      .then((response) => {
        setLogs(response.items);
        setTotalCount(response.totalCount);
      })
      .catch((error) => {
        console.error("Error al obtener logs:", error);
        setSnackbar({
          open: true,
          message: "Error al cargar los logs",
          severity: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        setInitialDataLoaded(true);
      });
  }, []);

  // Efecto para cargar logs cuando cambian dependencias
  useEffect(() => {
    loadLogsWithFilters(page, rowsPerPage, filtros);
  }, [page, rowsPerPage, loadLogsWithFilters, filtros]);

  // Manejar cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejar cambio en filas por página
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Manejar cambios en los filtros
  const handleInputChange = (field, value) => {
    // Actualizar filtros y resetear página
    setFiltros(prevFiltros => ({
      ...prevFiltros,
      [field]: value
    }));
    setPage(0);
  };

  // Limpiar todos los filtros
  const handleClearFilters = () => {
    const filtrosLimpios = {
      fechaDesde: null,
      fechaHasta: null,
      proceso: ""
    };
    
    setFiltros(filtrosLimpios);
    setPage(0);
  };

  // Exportar a Excel
  const handleExportToExcel = () => {
    setIsDownloading(true);
    
    const filtrosFormateados = formatearFiltrosParaEnvio(filtros);
    
    exportLogsToExcel(filtrosFormateados, localStorage.getItem("token"))
      .then(() => {
        setSnackbar({
          open: true,
          message: "Archivo Excel descargado correctamente",
          severity: "success",
        });
      })
      .catch((error) => {
        console.error("Error al exportar a Excel:", error);
        setSnackbar({
          open: true,
          message: "Error al exportar a Excel",
          severity: "error",
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      // Asegurar que la fecha se interprete como UTC si no tiene zona horaria
      const utcDate = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const date = new Date(utcDate);
      
      if (isNaN(date.getTime())) {
        console.warn('Fecha inválida:', dateString);
        return dateString; // Devolver string original si no se puede parsear
      }
      
      return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return dateString;
    }
  };

  const renderNoDataMessage = () => (
    <TableContainer component={Paper}>
      <Box p={3} sx={{ width: '100%' }}>
        <Typography variant="body1" color="text.secondary">
          No se encontraron registros de logs con los criterios de búsqueda actuales.
        </Typography>
      </Box>
    </TableContainer>
  );

  return (
    <Grid container direction="column" spacing={2} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
      <Grid item>
        <Typography variant="h4">Registros de Log</Typography>
      </Grid>

      <Grid item>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha desde"
                value={filtros.fechaDesde}
                onChange={(date) => handleInputChange('fechaDesde', date)}
                slotProps={{ textField: { sx: { width: 170 } } }}
                maxDate={filtros.fechaHasta || undefined}
              />
            </LocalizationProvider>
            
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha hasta"
                value={filtros.fechaHasta}
                onChange={(date) => handleInputChange('fechaHasta', date)}
                slotProps={{ textField: { sx: { width: 170 } } }}
                minDate={filtros.fechaDesde || undefined}
              />
            </LocalizationProvider>
            
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id="proceso-label">Proceso</InputLabel>
              <Select
                labelId="proceso-label"
                value={filtros.proceso}
                label="Proceso"
                onChange={(e) => handleInputChange('proceso', e.target.value)}
              >
                {procesosOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option || "Todos"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
              disabled={isLoading}
            >
              LIMPIAR
            </Button>
            
            <Button
              variant="contained"
              color="success"
              onClick={handleExportToExcel}
              disabled={isDownloading}
              startIcon={<DownloadIcon />}
            >
              {isDownloading ? <CircularProgress size={24} /> : "Excel"}
            </Button>
          </Box>
      </Grid>

      <Grid item>
        {(!initialDataLoaded && isLoading) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : logs.length > 0 ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Secuencia</TableCell>
                    <TableCell>Proceso</TableCell>
                    <TableCell>Mensaje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.id}</TableCell>
                      <TableCell>{formatDate(log.fecha)}</TableCell>
                      <TableCell>{log.secuencia}</TableCell>
                      <TableCell>{log.proceso}</TableCell>
                      <TableCell>{log.mensaje}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        ) : (
          renderNoDataMessage()
        )}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default GestorLogs;